import { IconCircleCheck, IconDownload, IconDownloadOff, IconPlaylistAdd, IconPlaylistX, IconTrashX } from '@tabler/icons-react';
import Button from '../../../shared/Button';
import Checkbox from '../../../shared/Checkbox';
import styles from './ModElement.module.css';
import { Mod, ModScope, ModState } from '../../../features/Mod/types/mod';
import { workshopService } from '../../../features/Workshop/workshopService';
import { modService } from '../../../features/Mod/modService';
import { useAtomValue } from 'jotai';
import { modsAtom } from '../../../features/Mod/modStore';
import Select from '../../../shared/Select';
import DropdownMenu from '../../../shared/DropdownMenu';
import { collectionsAtom } from '../../../features/Collection/collectionStore';
import { collectionService } from '../../../features/Collection/collectionService';
import { Collection } from '../../../features/Collection/types/collection';

type Props = {
	mod: Mod;
	parentCollection?: Collection;
};

const ModElement = ({ mod, parentCollection }: Props) =>
{
	const downloadedMods = useAtomValue(modsAtom).filter(m => m.state !== ModState.Collection);
	const collections = useAtomValue(collectionsAtom);

	const getDownloadPercentage = (): string =>
	{
		if (!mod.downloadProgress) return "0% downloaded";
		return `${Math.round(mod.downloadProgress * 100)}% downloaded`;
	};

	const getDownloadedSize = (): string =>
	{

		if (mod.state === ModState.Downloading)
		{
			if (!mod.downloadProgress || !mod.reportedSize) return "0 / 0 KB";

			const downloadedSize = mod.reportedSize * mod.downloadProgress;
			if (mod.reportedSize < 1024 * 1024)
				return `${(downloadedSize / 1024).toFixed(2)} / ${(mod.reportedSize / 1024).toFixed(2)} KB`;
			if (mod.reportedSize < 1024 * 1024 * 1024)
				return `${(downloadedSize / 1024 / 1024).toFixed(2)} / ${(mod.reportedSize / 1024 / 1024).toFixed(2)} MB`;
			return `${(downloadedSize / 1024 / 1024 / 1024).toFixed(2)} / ${(mod.reportedSize / 1024 / 1024 / 1024).toFixed(2)} GB`;
		} else
		{
			if (!mod.reportedSize) return "0 KB";
			if (mod.reportedSize < 1024 * 1024)
				return `${(mod.reportedSize / 1024).toFixed(2)} KB`;
			if (mod.reportedSize < 1024 * 1024 * 1024)
				return `${(mod.reportedSize / 1024 / 1024).toFixed(2)} MB`;
			return `${(mod.reportedSize / 1024 / 1024 / 1024).toFixed(2)} GB`;
		}
	};

	const handleDownload = () =>
	{
		workshopService.download(mod.id);
	};

	const handleSelect = () =>
	{
		if (mod.state == ModState.Selected)
		{
			modService.unselect(mod);
		} else
		{
			modService.select(mod);
		}
	};

	const handleScopeChange = (value: string) =>
	{
		const newScope = value as ModScope;
		modService.update({ ...mod, scope: newScope });
	};

	const handleDelete = () =>
	{
		modService.remove(mod.id);
	};

	const handleAddToCollection = (collectionId: string) =>
	{
		collectionService.addMod(collectionId, mod.id);
	};

	const handleRemoveFromCollection = () =>
	{
		if (!parentCollection) return;
		collectionService.removeMod(parentCollection.id, mod.id);
	};

	switch (mod.state)
	{
		case ModState.Downloaded:
		case ModState.Selected:
			return (
				<div className={styles.modElement}>
					<div className={styles.content}>
						<img className={styles.image} src={mod.image} />
						<div className={styles.modInfo}>
							<div>{mod.title}</div>
							<div className={styles.metaData}>
								<div>{mod.workshopUpdateDate.toUTCString()}</div>
								<div>{getDownloadedSize()}</div>
								{/* <div>{isSigned ? "Signed with server key" : "Missing server key"}</div> */}
							</div>
						</div>
						<div className={styles.controls}>
							<div className={styles.selectControls}>
								{mod.state === ModState.Selected && <Select options={[
									{ value: ModScope.Required, label: "Required" },
									{ value: ModScope.Optional, label: "Optional" },
									{ value: ModScope.Server, label: "Server" }
								]} value={mod.scope ?? ModScope.Required} onSelect={handleScopeChange} frameless />}
								<Checkbox checked={mod.state === ModState.Selected} onChange={handleSelect} />
							</div>
							{!parentCollection &&
								<div className={styles.actionButtons}>
									<DropdownMenu icon={<IconPlaylistAdd />} items={collections.map(collection => ({ label: collection.name, onClick: () => handleAddToCollection(collection.id) }))} />
									<Button icon={<IconTrashX />} onClick={handleDelete} frameless />
								</div>
							}
							{parentCollection &&
								<div className={styles.actionButtons}>
									<Button icon={<IconPlaylistX />} onClick={handleRemoveFromCollection} frameless />
								</div>
							}
						</div>
					</div>
				</div>
			);
		case ModState.Downloading:
			return (
				<div className={styles.modElement}>
					<div className={styles.progressFill} style={{ width: `${mod.downloadProgress ? mod.downloadProgress * 100 : 0}%` }}></div>
					<div className={styles.content}>
						<img className={styles.image} src={mod.image} />
						<div className={styles.modInfo}>
							<div>{mod.title}</div>
							<div className={styles.metaData}>
								<div>{mod.workshopUpdateDate.toUTCString()}</div>
								<div>{mod.reportedSize && (mod.reportedSize / 1024 / 1024).toFixed((mod.reportedSize / 1024 / 1024) < 10 ? 2 : 0) + " MB"}</div>
							</div>
						</div>
						<div className={styles.downloadInfo}>
							<div>{getDownloadPercentage()}</div>
							<div>{getDownloadedSize()}</div>
						</div>
						<Button icon={<IconDownloadOff />} onClick={handleDelete} />
					</div>
				</div>
			);
		default:
			const isDownloaded = downloadedMods.some(m => m.id === mod.id);

			return (
				<div className={styles.modElement}>
					<div className={styles.content}>
						<img className={styles.image} src={mod.image} />
						<div className={styles.modInfo}>
							<div>{mod.title}</div>
							<div className={styles.metaData}>
								<div>{mod.postedDate?.toUTCString()}</div>
								<div>{mod.workshopUpdateDate.toUTCString()}</div>
								<div>{mod.reportedSize && (mod.reportedSize / 1024 / 1024).toFixed((mod.reportedSize / 1024 / 1024) < 10 ? 2 : 0) + " MB"}</div>
							</div>
							<div className={styles.tags}>
								{mod.tags?.filter(tag => tag !== 'Mod').map(tag => <div key={tag}>{tag}</div>)}
							</div>
						</div>
						{isDownloaded ? <IconCircleCheck className={styles.downloadedIcon} /> : <Button icon={<IconDownload />} onClick={handleDownload} />}
					</div>
				</div>
			);
	}
};

export default ModElement;